import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import type { AdminUpdateNoteTemplateRequest } from '../store/hooks';

export type PreviousNoteScopeValue = NonNullable<
  AdminUpdateNoteTemplateRequest['previous_note_scope']
>;

interface PreviousNoteScopeControlProps {
  value: PreviousNoteScopeValue;
  onChange: (value: PreviousNoteScopeValue) => void;
  disabled?: boolean;
  error?: string;
  legacyDefault?: boolean;
}

const OPTIONS = [
  {
    value: 'any_note',
    label: 'Any recent note',
    description: 'Use the most recent completed note, regardless of its template.',
  },
  {
    value: 'same_template',
    label: 'Same template only',
    description: 'Keep continuity within notes created from this template.',
  },
  {
    value: 'none',
    label: 'Do not use a previous note',
    description: 'Generate without carrying forward narrative from an earlier note.',
  },
] as const satisfies ReadonlyArray<{
  value: PreviousNoteScopeValue;
  label: string;
  description: string;
}>;

const INCLUDED_CONTEXT: Record<PreviousNoteScopeValue, string> = {
  any_note: 'The latest completed note may be included.',
  same_template: 'Only a completed note from this template may be included.',
  none: 'Previous-note narrative is excluded.',
};

export function PreviousNoteScopeControl({
  value,
  onChange,
  disabled = false,
  error,
  legacyDefault = false,
}: PreviousNoteScopeControlProps) {
  const helperId = 'previous-note-scope-context';

  return (
    <FormControl fullWidth disabled={disabled} error={Boolean(error)}>
      <FormLabel id="previous-note-scope-label">Previous note context</FormLabel>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
        Choose which earlier note can inform generation for this template.
      </Typography>
      <RadioGroup
        aria-labelledby="previous-note-scope-label"
        aria-describedby={helperId}
        value={value}
        onChange={event => onChange(event.target.value as PreviousNoteScopeValue)}
      >
        {OPTIONS.map(option => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            sx={{ alignItems: 'flex-start', mx: 0, py: 0.5 }}
            label={
              <Box sx={{ pt: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              </Box>
            }
          />
        ))}
      </RadioGroup>

      <Box id={helperId} sx={{ mt: 1.25, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
          Context included
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {INCLUDED_CONTEXT[value]} Patient details, payer context, diagnoses, medications, and
          patient intelligence remain available.
        </Typography>
        {legacyDefault && value === 'any_note' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Legacy template default: no saved setting was found.
          </Typography>
        )}
      </Box>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}
